import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { LoaderCircle } from 'lucide-react'
import * as React from 'react'

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0 relative cursor-pointer',
    'font-heading uppercase tracking-[0.5px] font-medium',
    // `active:scale-[0.97]` is the press feedback. Without it a button gives no
    // acknowledgement until whatever it triggered finishes — which on a slow
    // network reads as a dropped tap, and gets tapped again. `transform` has to
    // be named in the transition list for the release to animate back.
    'transition-[color,background-color,border-color,transform] duration-150 ease-out',
    'active:scale-[0.97] outline-none select-none',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-40',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        dark: 'border border-primary bg-background text-primary hover:bg-primary/10 active:bg-primary/20',
        white:
          'border border-foreground bg-foreground text-background hover:bg-foreground/90 active:bg-foreground/80',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        gold: 'bg-gold text-black hover:bg-gold/90',
        purple: 'bg-purple text-purple-foreground hover:bg-purple/90',
        outline:
          'border border-border text-foreground hover:border-foreground/40 hover:bg-white/[0.06]',
        'outline-primary': 'border border-primary text-primary hover:bg-primary/10',
        'outline-purple': 'border border-purple text-purple hover:bg-purple/10',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-white/[0.08]',
        ghost: 'hover:bg-white/[0.06] hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        'primary-gradient': 'bg-primary text-primary-foreground hover:bg-primary-hover',
        'secondary-gradient': 'bg-secondary text-secondary-foreground hover:bg-white/[0.08]',
      },
      size: {
        xs: 'h-6 px-3 text-xs',
        sm: 'h-9 px-3',
        default: 'h-[50px] px-4 text-base',
        lg: 'h-[50px] px-6 text-lg',
        xl: 'h-14 px-8 text-xl',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean // added loading prop
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
        disabled={props.disabled || loading} // automatically disable on loading
      >
        {loading && <LoaderCircle className="absolute inset-0 m-auto size-5 animate-spin" />}
        <span
          className="inline-flex size-full items-center justify-center gap-2"
          style={{ visibility: loading ? 'hidden' : 'visible' }}
        >
          {children}
        </span>
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
